import {
  Badge,
  Button,
  Card,
  Dialog,
  Disclosure,
  Drawer,
  Input,
  Label,
  PageHeader,
  Select,
  cn,
} from "@safeplate/ui";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { ChevronRight, PackageCheck, X } from "lucide-react";
import { useEffect, useMemo, useState, type FormEvent } from "react";

import { displayError } from "../api/errors.js";
import { EmptyState, ErrorState, LoadingState } from "../components/states.js";
import { RecordDrawer } from "../records/records-page.js";
import { useShell } from "../shell/shell-context.js";
import {
  attentionGroupLabels,
  attentionGroupOf,
  groupFollowUps,
  isAttentionGroup,
  type AttentionGroupKey,
} from "./attention-groups.js";
import {
  clusterMissed,
  describeFollowUp,
  whenLabel,
  type AttentionRow,
  type MissedCluster,
} from "./attention-rows.js";
import {
  closeFollowUps,
  createDisposition,
  listAllOpenFollowUps,
  listFollowUps,
  resolveFollowUp,
  respondToFollowUp,
  type FollowUp,
  type FollowUpDecision,
  type FollowUpStatus,
  type FoodDisposition,
} from "./followups-api.js";
import { incidentSearch, type IncidentSearch } from "./record-incident.js";
import { useAttentionLookups } from "./use-attention-lookups.js";

const NOTE_LIMIT = 4000;

const decisionLabels: Record<FollowUpDecision, string> = {
  accepted_variance: "Accepted variance",
  dismissed: "Dismissed",
  edited_record: "Record corrected",
  escalated: "Escalated",
  resolved: "Resolved",
};

const dispositionLabels: Record<FoodDisposition["decision"], string> = {
  discard: "Discard",
  quarantine: "Quarantine",
  release: "Release",
  rework: "Rework",
};

function SeverityBadge({ severity }: { severity: AttentionRow["severity"] }) {
  if (!severity) return null;
  return (
    <Badge tone={severity === "critical" ? "danger" : "attention"}>
      {severity === "critical" ? "Critical" : "Major"}
    </Badge>
  );
}

function RowText({ row }: { row: AttentionRow }) {
  return (
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <p className="text-sm font-semibold text-stone-950">{row.title}</p>
        <SeverityBadge severity={row.severity} />
      </div>
      <p className="mt-0.5 text-xs text-stone-500">{row.detail.join(" · ")}</p>
      {row.response ? (
        <p className="mt-1 text-xs text-stone-700">“{row.response}”</p>
      ) : null}
    </div>
  );
}

export function AttentionItem({
  canClose,
  closing,
  onClose,
  onOpen,
  row,
}: {
  canClose: boolean;
  closing: boolean;
  onClose: () => void;
  onOpen: () => void;
  row: AttentionRow;
}) {
  return (
    <div className="flex items-center gap-2 pr-3 sm:pr-4">
      <button
        aria-label={[row.title, ...row.detail, row.response]
          .filter(Boolean)
          .join(", ")}
        className="focus-inset flex min-w-0 flex-1 items-center gap-3 py-3 pl-4 text-left transition hover:bg-stone-50 sm:pl-5"
        onClick={onOpen}
        type="button"
      >
        <RowText row={row} />
        <ChevronRight
          aria-hidden="true"
          className="size-4 shrink-0 text-stone-400"
        />
      </button>
      {canClose ? (
        <Button
          aria-label={`Close ${row.title}`}
          disabled={closing}
          onClick={onClose}
          size="sm"
          variant="outline"
        >
          {closing ? "Closing…" : "Close"}
        </Button>
      ) : null}
    </div>
  );
}

function MissedClusterRow({
  canClose,
  closing,
  closingId,
  cluster,
  onClose,
  onCloseOne,
  onOpen,
  timezone,
}: {
  canClose: boolean;
  closing: boolean;
  closingId: string | null | undefined;
  cluster: MissedCluster;
  onClose: () => void;
  onCloseOne: (id: string) => void;
  onOpen: (id: string) => void;
  timezone: string;
}) {
  const [open, setOpen] = useState(false);
  const single = cluster.items.length === 1;
  const row: AttentionRow = {
    detail: [cluster.detail],
    response: null,
    severity: cluster.severity,
    title: cluster.title,
  };
  return (
    <div>
      <div className="flex items-center gap-2 pr-3 sm:pr-4">
        <button
          aria-expanded={single ? undefined : open}
          aria-label={`${cluster.title}, ${cluster.detail}`}
          className="focus-inset flex min-w-0 flex-1 items-center gap-3 py-3 pl-4 text-left transition hover:bg-stone-50 sm:pl-5"
          onClick={() =>
            single ? onOpen(cluster.items[0]!.id) : setOpen((value) => !value)
          }
          type="button"
        >
          <RowText row={row} />
          <ChevronRight
            aria-hidden="true"
            className={cn(
              "size-4 shrink-0 text-stone-400 transition-transform",
              open && "rotate-90",
            )}
          />
        </button>
        {canClose ? (
          <Button
            aria-label={`Close ${cluster.title}`}
            disabled={closing}
            onClick={onClose}
            size="sm"
            variant="outline"
          >
            {closing
              ? "Closing…"
              : single
                ? "Close"
                : `Close ${cluster.items.length}`}
          </Button>
        ) : null}
      </div>
      {open && !single ? (
        <ul className="divide-y divide-stone-100 border-t border-stone-100 bg-canvas/40">
          {cluster.items.map((item, index) => (
            <li
              className="flex items-center gap-2 pr-3 pl-6 sm:pr-4 sm:pl-9"
              key={item.id}
            >
              <button
                className="min-w-0 flex-1 py-2.5 text-left focus-inset text-sm text-stone-700 hover:text-stone-950"
                onClick={() => onOpen(item.id)}
                type="button"
              >
                {whenLabel(cluster.dues[index] ?? item.created_at, timezone)}
              </button>
              {canClose ? (
                <Button
                  disabled={closingId === item.id}
                  onClick={() => onCloseOne(item.id)}
                  size="sm"
                  variant="ghost"
                >
                  Close
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function FollowUpDrawer({
  canManage,
  followUp,
  incident,
  onChanged,
  onClose,
  row,
}: {
  canManage: boolean;
  followUp: FollowUp;
  incident: IncidentSearch;
  onChanged: (followUp: FollowUp) => void;
  onClose: () => void;
  row: AttentionRow;
}) {
  const navigate = useNavigate();
  // The record this follow-up is about, or the incident it was escalated to.
  const [shownRecord, setShownRecord] = useState<string | null>(null);
  const [dispositions, setDispositions] = useState<FoodDisposition[]>([]);
  const respond = useMutation({
    mutationFn: (response: string) => respondToFollowUp(followUp.id, response),
    onSuccess: onChanged,
  });
  const resolve = useMutation({
    mutationFn: (form: FormData) =>
      resolveFollowUp(followUp.id, {
        decision: String(form.get("decision")) as FollowUpDecision,
        note: String(form.get("note") ?? "").trim() || null,
      }),
    onSuccess: onChanged,
  });
  const disposition = useMutation({
    mutationFn: (form: FormData) =>
      createDisposition(followUp.id, {
        batch_lot: String(form.get("batch_lot") ?? "").trim() || null,
        decision: String(form.get("decision")) as FoodDisposition["decision"],
        note: String(form.get("note") ?? "").trim() || null,
        product: String(form.get("product") ?? "").trim(),
        quantity: String(form.get("quantity") ?? "").trim() || null,
      }),
    onSuccess: (created) => setDispositions((current) => [...current, created]),
  });
  const foodDecision = attentionGroupOf(followUp) === "needs_decision";

  return (
    <Drawer label={row.title} onClose={onClose}>
      <div>
        <header className="flex items-start justify-between gap-3 border-b border-stone-200 px-5 py-4 sm:px-6">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-semibold text-stone-950">
                {row.title}
              </h2>
              <SeverityBadge severity={row.severity} />
            </div>
            <p className="mt-1 text-sm text-stone-500">
              {row.detail.join(" · ")}
            </p>
          </div>
          <Button
            aria-label="Close panel"
            autoFocus
            onClick={onClose}
            size="icon"
            variant="ghost"
          >
            <X className="size-4" />
          </Button>
        </header>
        <div className="space-y-5 px-5 py-5 sm:px-6">
          {followUp.suggested_action && followUp.status !== "closed" ? (
            <p className="text-sm text-stone-700">
              {followUp.suggested_action}
            </p>
          ) : null}
          {row.response ? (
            <div>
              <h3 className="text-xs font-semibold text-stone-500">
                Staff response
              </h3>
              <p className="mt-1 text-sm text-stone-900">“{row.response}”</p>
            </div>
          ) : null}
          {followUp.record_id ? (
            <Button
              onClick={() => setShownRecord(followUp.record_id)}
              variant="outline"
            >
              Open record
            </Button>
          ) : null}
          {followUp.status === "closed" ? (
            <div className="border-t border-stone-200 pt-4">
              <p className="text-sm font-semibold text-stone-950">
                {followUp.manager_decision
                  ? decisionLabels[followUp.manager_decision]
                  : "Closed"}
              </p>
              {followUp.manager_note ? (
                <p className="mt-1 text-sm text-stone-600">
                  {followUp.manager_note}
                </p>
              ) : null}
              {followUp.escalated_incident_record_id ? (
                <Button
                  className="mt-3"
                  onClick={() =>
                    setShownRecord(followUp.escalated_incident_record_id!)
                  }
                  variant="outline"
                >
                  Open incident
                </Button>
              ) : null}
            </div>
          ) : null}

          {dispositions.map((item) => (
            <p
              className="flex items-center gap-2 text-sm text-stone-800"
              key={item.id}
            >
              <PackageCheck
                aria-hidden="true"
                className="size-4 text-emerald-800"
              />
              {item.product} · {dispositionLabels[item.decision]}
            </p>
          ))}

          {!canManage && followUp.status !== "closed" ? (
            <form
              className="space-y-2 border-t border-stone-200 pt-4"
              onSubmit={(event: FormEvent<HTMLFormElement>) => {
                event.preventDefault();
                respond.mutate(
                  String(
                    new FormData(event.currentTarget).get("response") ?? "",
                  ),
                );
              }}
            >
              <Label htmlFor="staff-response">Your response</Label>
              <textarea
                className="min-h-24 w-full rounded-md border border-stone-300 bg-surface p-3 text-sm"
                id="staff-response"
                name="response"
                required
              />
              {respond.error ? (
                <p className="text-sm font-semibold text-red-800" role="alert">
                  {displayError(respond.error)}
                </p>
              ) : null}
              <div className="text-right">
                <Button disabled={respond.isPending} type="submit">
                  {respond.isPending ? "Sending…" : "Send"}
                </Button>
              </div>
            </form>
          ) : null}

          {canManage && followUp.status !== "closed" ? (
            <>
              {foodDecision ? (
                <Disclosure summary="Food decision">
                  <form
                    className="grid gap-3 sm:grid-cols-2"
                    onSubmit={(event: FormEvent<HTMLFormElement>) => {
                      event.preventDefault();
                      disposition.mutate(new FormData(event.currentTarget));
                      event.currentTarget.reset();
                    }}
                  >
                    <div className="sm:col-span-2">
                      <Label htmlFor="disposition-product">Food</Label>
                      <Input
                        className="mt-1"
                        id="disposition-product"
                        name="product"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="disposition-decision">Decision</Label>
                      <Select
                        id="disposition-decision"
                        name="decision"
                        wrapperClassName="mt-1"
                      >
                        {Object.entries(dispositionLabels).map(
                          ([value, label]) => (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          ),
                        )}
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="disposition-quantity">Quantity</Label>
                      <Input
                        className="mt-1"
                        id="disposition-quantity"
                        name="quantity"
                      />
                    </div>
                    <div>
                      <Label htmlFor="disposition-batch">Batch or lot</Label>
                      <Input
                        className="mt-1"
                        id="disposition-batch"
                        name="batch_lot"
                      />
                    </div>
                    <div>
                      <Label htmlFor="disposition-note">Note</Label>
                      <Input
                        className="mt-1"
                        id="disposition-note"
                        name="note"
                      />
                    </div>
                    {disposition.error ? (
                      <p
                        className="text-sm font-semibold text-red-800 sm:col-span-2"
                        role="alert"
                      >
                        {displayError(disposition.error)}
                      </p>
                    ) : null}
                    <div className="text-right sm:col-span-2">
                      <Button
                        disabled={disposition.isPending}
                        size="sm"
                        type="submit"
                        variant="outline"
                      >
                        {disposition.isPending
                          ? "Saving…"
                          : "Save food decision"}
                      </Button>
                    </div>
                  </form>
                </Disclosure>
              ) : null}
              <form
                className="space-y-4 border-t border-stone-200 pt-4"
                onSubmit={(event: FormEvent<HTMLFormElement>) => {
                  event.preventDefault();
                  resolve.mutate(new FormData(event.currentTarget));
                }}
              >
                <div>
                  <Label htmlFor="manager-decision">Outcome</Label>
                  <Select
                    id="manager-decision"
                    name="decision"
                    wrapperClassName="mt-1.5"
                  >
                    {Object.entries(decisionLabels)
                      .sort(([a], [b]) =>
                        a === "resolved" ? -1 : b === "resolved" ? 1 : 0,
                      )
                      .map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                  </Select>
                </div>
                <div>
                  <Label htmlFor="manager-note">
                    {followUp.record_id ? "Report note" : "Note"}
                  </Label>
                  <textarea
                    className="mt-1.5 min-h-20 w-full rounded-md border border-stone-300 bg-surface p-3 text-sm"
                    id="manager-note"
                    maxLength={NOTE_LIMIT}
                    name="note"
                  />
                </div>
                {resolve.error ? (
                  <p
                    className="text-sm font-semibold text-red-800"
                    role="alert"
                  >
                    {displayError(resolve.error)}
                  </p>
                ) : null}
                <div className="flex flex-wrap justify-end gap-2">
                  {foodDecision ? (
                    <Button
                      disabled={resolve.isPending}
                      onClick={() =>
                        void navigate({ search: incident, to: "/log" })
                      }
                      type="button"
                    >
                      Record as incident
                    </Button>
                  ) : null}
                  <Button disabled={resolve.isPending} type="submit">
                    {resolve.isPending ? "Closing…" : "Close"}
                  </Button>
                </div>
              </form>
            </>
          ) : null}
        </div>
      </div>
      {shownRecord ? (
        <RecordDrawer
          canManage={canManage}
          onClose={() => setShownRecord(null)}
          recordId={shownRecord}
        />
      ) : null}
    </Drawer>
  );
}

function CloseAllDialog({
  count,
  group,
  hasMore,
  locationId,
  onClose,
  onDone,
}: {
  count: number;
  group: AttentionGroupKey;
  hasMore: boolean;
  locationId: string;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const close = useMutation({
    mutationFn: async () => {
      const open = await listAllOpenFollowUps(locationId);
      return closeFollowUps(
        open
          .filter((item) => attentionGroupOf(item) === group)
          .map((item) => item.id),
      );
    },
    onSuccess: async (result) => {
      await onDone();
      if (result.failed === 0) onClose();
    },
  });
  const label = attentionGroupLabels[group].toLowerCase();
  return (
    <Dialog
      footer={
        <>
          <Button disabled={close.isPending} onClick={onClose} variant="ghost">
            Cancel
          </Button>
          <Button disabled={close.isPending} onClick={() => close.mutate()}>
            {close.isPending ? "Closing…" : "Close all"}
          </Button>
        </>
      }
      onClose={onClose}
      open
      size="sm"
      title={`Close all ${label}?`}
    >
      <p className="text-sm text-stone-700">
        {hasMore
          ? `${count}+ items`
          : count === 1
            ? "1 item"
            : `${count} items`}
      </p>
      {close.data && close.data.failed > 0 ? (
        <p className="mt-3 text-sm font-semibold text-red-800" role="alert">
          {close.data.failed} couldn’t be closed. Try again.
        </p>
      ) : null}
      {close.error ? (
        <p className="mt-3 text-sm font-semibold text-red-800" role="alert">
          {displayError(close.error)}
        </p>
      ) : null}
    </Dialog>
  );
}

export function AttentionWorkspacePage() {
  const shell = useShell();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const locationId = shell.activeLocation?.id;
  const timezone = shell.activeLocation?.timezone ?? "Pacific/Auckland";
  const canManage = shell.activeRole !== "staff";
  const itemParam = typeof search.item === "string" ? search.item : null;
  const groupParam = isAttentionGroup(search.group as string)
    ? (search.group as AttentionGroupKey)
    : null;
  const [status, setStatus] = useState<FollowUpStatus | "">("open");
  const [updated, setUpdated] = useState<FollowUp | null>(null);
  const [closingAll, setClosingAll] = useState<AttentionGroupKey | null>(null);
  const followUps = useInfiniteQuery({
    enabled: Boolean(locationId),
    getNextPageParam: (page: Awaited<ReturnType<typeof listFollowUps>>) =>
      page.next_cursor ?? undefined,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      listFollowUps(locationId!, status ? { status } : {}, pageParam),
    queryKey: ["follow-ups", locationId, status],
  });
  const items = useMemo(
    () =>
      followUps.data?.pages.flatMap(
        (page: Awaited<ReturnType<typeof listFollowUps>>) => page.items,
      ) ?? [],
    [followUps.data],
  );
  const lookups = useAttentionLookups(locationId, items);
  const groups = useMemo(() => groupFollowUps(items), [items]);
  const selected = itemParam
    ? ((updated?.id === itemParam ? updated : null) ??
      items.find((entry) => entry.id === itemParam) ??
      null)
    : null;
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["follow-ups"] }),
      queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
    ]).then(() => undefined);
  const closeOne = useMutation({
    mutationFn: (id: string) => resolveFollowUp(id, { decision: "resolved" }),
    onSuccess: refresh,
  });
  const closeMany = useMutation({
    mutationFn: (ids: string[]) => closeFollowUps(ids),
    onSettled: refresh,
  });

  function go(next: {
    group?: AttentionGroupKey | null;
    item?: string | null;
  }) {
    const group = next.group === undefined ? groupParam : next.group;
    const item = next.item === undefined ? itemParam : next.item;
    void navigate({
      search: {
        ...(group ? { group } : {}),
        ...(item ? { item } : {}),
      },
      to: "/attention",
    });
  }

  // A linked item that is not in the open list may be further back or closed.
  useEffect(() => {
    if (!itemParam || selected) return;
    if (followUps.isPending || followUps.isFetching) return;
    if (followUps.hasNextPage) void followUps.fetchNextPage();
    else if (status !== "") setStatus("");
  }, [followUps, itemParam, selected, status]);

  const visibleGroups = groups.filter(
    (group) =>
      group.items.length > 0 && (!groupParam || group.key === groupParam),
  );
  const showClose = canManage && status !== "closed";

  return (
    <>
      <PageHeader
        actions={
          <Select
            aria-label="Status"
            onChange={(event) =>
              setStatus(event.target.value as FollowUpStatus | "")
            }
            size="sm"
            value={status}
            wrapperClassName="w-36"
          >
            <option value="open">Open</option>
            <option value="in_progress">In progress</option>
            <option value="closed">Closed</option>
            <option value="">All</option>
          </Select>
        }
        className="mb-5"
        title="Attention"
      />
      {!locationId ? (
        <EmptyState title="Choose a location" />
      ) : (
        <div className="space-y-5">
          {groupParam ? (
            <Button
              onClick={() => go({ group: null })}
              size="sm"
              variant="ghost"
            >
              Show everything
            </Button>
          ) : null}
          {followUps.isPending ? <LoadingState label="Loading" /> : null}
          {followUps.error ? (
            <ErrorState
              message={displayError(followUps.error)}
              onAction={() => void followUps.refetch()}
              title="Attention isn’t available"
            />
          ) : null}
          {closeOne.error ? (
            <p className="text-sm font-semibold text-red-800" role="alert">
              {displayError(closeOne.error)}
            </p>
          ) : null}
          {!followUps.isPending &&
          !followUps.error &&
          visibleGroups.length === 0 ? (
            <EmptyState
              calm
              title={
                status === "open" && !groupParam
                  ? "Nothing needs attention"
                  : "Nothing here"
              }
            />
          ) : null}
          {visibleGroups.map((group) => (
            <section key={group.key}>
              <div className="mb-2 flex items-center justify-between gap-3">
                <h2 className="text-base font-semibold text-stone-950">
                  {group.label}{" "}
                  <span className="font-normal text-stone-500">
                    {group.items.length}
                    {followUps.hasNextPage ? "+" : ""}
                  </span>
                </h2>
                {showClose && status === "open" && group.items.length > 1 ? (
                  <Button
                    onClick={() => setClosingAll(group.key)}
                    size="sm"
                    variant="ghost"
                  >
                    Close all
                  </Button>
                ) : null}
              </div>
              <Card className="overflow-hidden">
                <div className="divide-y divide-stone-200">
                  {group.key === "missed_checks"
                    ? clusterMissed(group.items, lookups, timezone).map(
                        (cluster) => (
                          <MissedClusterRow
                            canClose={showClose}
                            closing={
                              closeMany.isPending &&
                              closeMany.variables?.[0] === cluster.items[0]?.id
                            }
                            cluster={cluster}
                            closingId={
                              closeOne.isPending ? closeOne.variables : null
                            }
                            key={cluster.key}
                            onClose={() =>
                              closeMany.mutate(
                                cluster.items.map((item) => item.id),
                              )
                            }
                            onCloseOne={(id) => closeOne.mutate(id)}
                            onOpen={(id) => go({ item: id })}
                            timezone={timezone}
                          />
                        ),
                      )
                    : group.items.map((item) => (
                        <AttentionItem
                          canClose={showClose && item.status !== "closed"}
                          closing={
                            closeOne.isPending && closeOne.variables === item.id
                          }
                          key={item.id}
                          onClose={() => closeOne.mutate(item.id)}
                          onOpen={() => go({ item: item.id })}
                          row={describeFollowUp(item, lookups, timezone)}
                        />
                      ))}
                </div>
              </Card>
            </section>
          ))}
          {followUps.hasNextPage ? (
            <div className="text-center">
              <Button
                disabled={followUps.isFetchingNextPage}
                onClick={() => void followUps.fetchNextPage()}
                variant="outline"
              >
                {followUps.isFetchingNextPage ? "Loading…" : "Load older"}
              </Button>
            </div>
          ) : null}
        </div>
      )}
      {selected ? (
        <FollowUpDrawer
          canManage={canManage}
          followUp={selected}
          incident={incidentSearch(
            selected,
            describeFollowUp(selected, lookups, timezone),
            selected.record_id
              ? lookups.records.get(selected.record_id)?.record_type_code
              : undefined,
          )}
          onChanged={async (changed) => {
            setUpdated(changed);
            await refresh();
          }}
          onClose={() => {
            setUpdated(null);
            go({ item: null });
          }}
          row={describeFollowUp(selected, lookups, timezone)}
        />
      ) : null}
      {closingAll && locationId ? (
        <CloseAllDialog
          count={
            groups.find((group) => group.key === closingAll)?.items.length ?? 0
          }
          group={closingAll}
          hasMore={followUps.hasNextPage}
          locationId={locationId}
          onClose={() => setClosingAll(null)}
          onDone={refresh}
        />
      ) : null}
    </>
  );
}
